import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  IconArrowRight, IconCalendarCheck, IconHistory, IconBell, IconClock, IconMapPin, IconPhone,
} from '@tabler/icons-react';
import PublicHeader from '../../components/layout/PublicHeader';
import SiteFooter from '../../components/layout/SiteFooter';
import Button from '../../components/common/Button';
import DoctorPhoto from '../../components/common/DoctorPhoto';
import HeroBookingPreview from './HeroBookingPreview';
import doctorApi from '../../api/doctor/doctorApi';
import useFetch from '../../hooks/useFetch';
import { useClinic } from '../../context/ClinicContext';
import { useAuth } from '../../context/AuthContext';
import { PATHS, toPath } from '../../routes/paths';
import { formatPhone } from '../../utils/format';

const FEATURE_ICONS = {
  calendar: { Icon: IconCalendarCheck },
  history: { Icon: IconHistory },
  reminder: { Icon: IconBell },
};

export default function LandingPage() {
  const { clinic, landing } = useClinic();
  const { isAuthenticated } = useAuth();
  const { data: doctors } = useFetch(() => doctorApi.list(), [], { initial: [] });

  if (isAuthenticated) return <Navigate to={PATHS.home} replace />;

  const featured = doctors.filter((d) => d.featured).sort((a, b) => (a.featuredRank || 99) - (b.featuredRank || 99));
  const heroDoctor = featured.find((d) => d.nextAvailable) || doctors.find((d) => d.nextAvailable);
  const phone = clinic ? formatPhone(clinic.phone.countryCode, clinic.phone.number) : '';

  return (
    <div className="page page--white landing">
      <PublicHeader />

      <section className="landing__hero">
        {landing?.badge && <span className="badge badge--outline">{landing.badge}</span>}
        <h1 className="landing__title">{landing?.title}</h1>
        <p className="landing__subtitle">{landing?.subtitle}</p>
        <div className="landing__ctas">
          <Button block to={PATHS.doctors}>
            Book an appointment
            <IconArrowRight size={20} />
          </Button>
          <Button block variant="outline" to={PATHS.register}>Create an account</Button>
        </div>
        <HeroBookingPreview doctor={heroDoctor} />
      </section>

      <section className="landing__section">
        <h2 className="landing__h2">Everything for your visit</h2>
        <ul className="feature-list">
          {(landing?.features || []).map((f) => {
            const { Icon } = FEATURE_ICONS[f.icon] || FEATURE_ICONS.calendar;
            return (
              <li key={f.title} className="feature-list__item">
                <span className="feature-list__icon"><Icon size={22} /></span>
                <div>
                  <h3 className="feature-list__title">{f.title}</h3>
                  <p className="feature-list__body">{f.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="landing__section landing__section--tinted">
        <div className="section-head">
          <h2 className="landing__h2">Our doctors</h2>
          <Link to={PATHS.doctors} className="section-head__link">See all</Link>
        </div>
        <div className="doctor-rail">
          {(featured.length ? featured : doctors).map((d) => (
            <Link key={d.id} to={toPath(PATHS.doctorDetail, { id: d.id })} className="doctor-rail__card">
              <DoctorPhoto src={d.photoUrl} name={d.name} />
              <div className="doctor-rail__body">
                <p className="doctor-rail__name">{d.name}</p>
                <p className="doctor-rail__meta">{d.specialty}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="landing__section">
        <div className="how-it-works">
          <h2 className="how-it-works__title">How it works</h2>
          <ol>
            {(landing?.steps || []).map((s, i) => (
              <li key={s} className="how-it-works__step">
                <span className="how-it-works__num">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>

        <h2 className="landing__h2 u-mt-6">Visit us</h2>
        <div className="list-card u-mt-4">
          <div className="list-row">
            <span className="icon-tile"><IconClock size={20} /></span>
            <div className="list-row__body">
              <p className="list-row__title">Opening hours</p>
              <p className="list-row__sub">{clinic?.openingHours}</p>
            </div>
          </div>
          <div className="list-row">
            <span className="icon-tile"><IconMapPin size={20} /></span>
            <div className="list-row__body">
              <p className="list-row__title">Address</p>
              <p className="list-row__sub">{clinic?.address}</p>
            </div>
          </div>
          <div className="list-row">
            <span className="icon-tile"><IconPhone size={20} /></span>
            <div className="list-row__body">
              <p className="list-row__title">Phone</p>
              <a className="list-row__sub link" href={`tel:${clinic?.phone.countryCode}${clinic?.phone.number}`}>{phone}</a>
            </div>
          </div>
        </div>

        <Button block to={PATHS.register} className="u-mt-6">Get started</Button>
      </section>

      <SiteFooter />
    </div>
  );
}
